/**
 * La PROPUESTA automática del SIGPAC de una nave (27-09-2026, petición de Pablo):
 * «si la nave es de 0,09 ha, que busque en ese punto un recinto de 0,09 ha y lo
 * proponga para confirmar; si hay otros parecidos, que pregunte».
 *
 * COPIA DE CLIENTE. La del servidor es supabase/functions/_shared/sigpacPropuesta.ts
 * y tienen que ser IGUALES salvo esta cabecera (src/__tests__/sigpacPropuesta.test.ts,
 * «paridad»): la pantalla deriva el estado de las filas guardadas con esta regla, y la
 * edge (alta de la nave, relleno) decide con la otra qué guarda.
 */

/**
 * Tolerancia de «la superficie casa»: ±10 % de la nave, y nunca menos de 0,02 ha.
 *
 * Medida el 27-09-2026 contra el WMS del FEGA con las naves de la demo, Ulture QA y
 * la réplica de Nijasol (30 naves): las dibujadas SOBRE el recinto del SIGPAC casan
 * entre el 0,1 % y el 4,2 % (Las Norias · Nave 3: 1,0095 ha la nave, 1,009 el
 * recinto; Mojonera · Puerta 5-6: 0,642 y 0,670); las que NO son ese recinto se
 * van al 17 % o más (Puerta 8: 1,011 frente a 0,834, que pisa dos recintos). El
 * ±10 % deja margen a un calcado a mano sin tragarse la nave que ocupa dos recintos.
 * El mínimo de 0,02 ha es para las naves pequeñas (0,05 ha ± 10 % es medio área).
 */
export const TOLERANCIA_RELATIVA = 0.1;
export const TOLERANCIA_MINIMA_HA = 0.02;

/**
 * LA NAVE ALGO MAYOR QUE EL RECINTO (enmienda del 27-09-2026 por la tarde, Pablo:
 * «normalmente se dibuja a mano, porque las parcelas son un poco más grandes que lo
 * registrado»). Quien dibuja la nave sobre el satélite la bordea por fuera (canales,
 * bandas, el pasillo de servicio), así que el dibujo sale MAYOR que el recinto que
 * tiene registrado. Con solo el ±10 % esa nave se quedaba «dudosa» aunque cayera
 * entera en un recinto.
 *
 * Por eso, si NINGÚN recinto casa en superficie, se mira si hay UNO de uso
 * compatible del que la nave sea hasta un `EXCESO_MAX_NAVE` mayor y que se lleve al
 * menos `SOLAPE_ALTO` de los puntos consultados dentro del dibujo (la nave cae casi
 * toda en él): ese sale «propuesto» (motivo `nave_algo_mayor`), para confirmar.
 * Si cae ahí pero con menos solape, la nave seguramente ocupa más de un recinto:
 * «dudoso» (`nave_mayor_varios`), con los candidatos para marcar.
 *
 * Medido contra el WMS el 27-09 (Las Norias · Naves 2, 3 y 4, Campohermoso · Naves 4
 * y 7, Mojonera · Puertas 1-2, 5-6, 7 y 8, Las Norias · Nave 1) agrandando el dibujo
 * real: +15 % y +30 % alrededor, +20 % y +35 % por un lado. Casos apuntados en
 * scripts/ops/2026-09-27-sigpac-disenador-APLICAR.md y en el test.
 */
export const EXCESO_MAX_NAVE = 0.4;
export const SOLAPE_ALTO = 0.8;

/** Como mucho, estos candidatos se guardan para elegir (los más idóneos). */
export const MAX_CANDIDATOS = 5;

/**
 * Usos con los que se propone solo: IV (invernaderos y cultivos bajo plástico) y,
 * porque el SIGPAC no siempre está al día con los invernaderos nuevos, TA (tierra
 * arable) y TH (huerta). Con TA o TH la pantalla sigue avisando «no es IV». Un
 * camino (IM), una acequia (CA), frutales, forestal… nunca se proponen solos.
 */
export const USOS_COMPATIBLES = ["IV", "TA", "TH"] as const;

export type EstadoPropuesta = "propuesto" | "dudoso" | "sin_propuesta";

export type MotivoPropuesta =
  /** Un solo recinto de uso compatible con la superficie de la nave, y es donde cae la nave. */
  | "superficie_casa"
  /** La nave es algo mayor que UN recinto compatible y cae casi entera en él. */
  | "nave_algo_mayor"
  /** La nave es algo mayor que un recinto donde cae, pero no casi entera: ocupará varios. */
  | "nave_mayor_varios"
  /** Más de un recinto con una superficie parecida: se pregunta. */
  | "varios_parecidos"
  /** Ninguno tiene la superficie de la nave (varios recintos, o un recinto con varias naves). */
  | "ninguno_casa"
  /** El que casa en superficie no es de invernadero ni compatible (camino, acequia…). */
  | "uso_no_compatible"
  /** El que casa solo lo pisa un borde de la nave: la nave cae sobre todo en otro. */
  | "no_domina"
  /** La nave no tiene superficie con la que comparar. */
  | "sin_area"
  /** El SIGPAC no ha devuelto ningún recinto. */
  | "sin_candidatos";

/** Lo mínimo de un recinto candidato (una fila guardada o lo que dijo el WMS). */
export interface CandidatoSigpac {
  superficie_ha: number | string | null;
  uso_sigpac: string | null;
  /** Cuántos de los puntos consultados dentro de la nave cayeron en él. */
  muestras: number | null;
}

export interface PropuestaSigpac<T extends CandidatoSigpac> {
  estado: EstadoPropuesta;
  motivo: MotivoPropuesta;
  /** El recinto propuesto (solo con estado «propuesto»). */
  propuesto: T | null;
  /**
   * Todos los candidatos, del más IDÓNEO al que menos: primero los de uso
   * compatible, luego los que más puntos de la nave se llevan (el solape) y, a
   * igualdad, el que más se parece en superficie.
   */
  candidatos: T[];
  /** Superficie de la nave en ha (null si no se sabe). */
  area_nave_ha: number | null;
  /** Diferencia que se admite, en ha, para esa nave. */
  tolerancia_ha: number | null;
}

/** Área en ha de un anillo [lng, lat] (fórmula esférica, la misma de `sigpac-proxy`). null si no es un anillo. */
export function areaAnilloHa(ring: ReadonlyArray<readonly [number, number]> | null | undefined): number | null {
  if (!ring || ring.length < 3) return null;
  const RAD = Math.PI / 180;
  const R = 6371000;
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length;
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[j];
    if (![lng1, lat1, lng2, lat2].every(Number.isFinite)) return null;
    area += (lng2 - lng1) * RAD * (2 + Math.sin(lat1 * RAD) + Math.sin(lat2 * RAD));
  }
  const ha = Math.abs((area * R * R) / 2) / 10000;
  return ha > 0 ? ha : null;
}

export function toleranciaHa(areaNaveHa: number): number {
  return Math.max(areaNaveHa * TOLERANCIA_RELATIVA, TOLERANCIA_MINIMA_HA);
}

const num = (v: number | string | null | undefined): number | null => {
  if (v == null || (typeof v === "string" && v.trim() === "")) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** «IV - INVERNADEROS…» o «iv» → «IV». */
const uso = (u: string | null | undefined): string | null => {
  const m = /^\s*([A-Za-z]{2})\b/.exec(u ?? "");
  return m ? m[1].toUpperCase() : null;
};

export function usoCompatible(u: string | null | undefined): boolean {
  const c = uso(u);
  return c != null && (USOS_COMPATIBLES as readonly string[]).includes(c);
}

/** ¿Casa la superficie del recinto con la de la nave? */
export function casaSuperficie(areaNaveHa: number | null, superficieHa: number | string | null): boolean {
  const s = num(superficieHa);
  if (areaNaveHa == null || !(areaNaveHa > 0) || s == null || !(s > 0)) return false;
  return Math.abs(s - areaNaveHa) <= toleranciaHa(areaNaveHa) + 1e-9;
}

/**
 * ¿Es la nave ALGO MAYOR que el recinto? Más de la tolerancia (si no, «casa») y
 * como mucho un `EXCESO_MAX_NAVE` más: 0,83 ha de recinto admite naves de 0,93 a
 * 1,12 ha.
 */
export function naveAlgoMayor(areaNaveHa: number | null, superficieHa: number | string | null): boolean {
  const s = num(superficieHa);
  if (areaNaveHa == null || !(areaNaveHa > 0) || s == null || !(s > 0)) return false;
  return areaNaveHa - s > toleranciaHa(areaNaveHa) + 1e-9 && areaNaveHa <= s * (1 + EXCESO_MAX_NAVE) + 1e-9;
}

/** Parte de los puntos de la nave que caen en el candidato (null si no hay muestras). */
export function solape<T extends CandidatoSigpac>(c: T, todos: readonly T[]): number | null {
  const total = todos.reduce((s, x) => s + (x.muestras ?? 0), 0);
  return total > 0 ? (c.muestras ?? 0) / total : null;
}

/**
 * LA REGLA. Con la superficie de la nave y los recintos que el WMS encontró en su
 * centroide y dentro de su dibujo:
 *   - ninguno → «sin_propuesta»;
 *   - la nave sin superficie → «dudoso» (se pregunta con los candidatos);
 *   - UNO que casa en superficie (± `toleranciaHa`), de uso compatible, y donde cae
 *     al menos la mitad de los puntos → «propuesto»;
 *   - si casan varios, o el que casa no es de uso compatible o solo lo pisa un
 *     borde → «dudoso», con los candidatos para elegir;
 *   - si no casa ninguno pero la nave es ALGO MAYOR que UNO compatible donde cae
 *     casi entera (`SOLAPE_ALTO`) → «propuesto» (`nave_algo_mayor`); con menos
 *     solape, o varios así → «dudoso»; si no hay ninguno así → «dudoso»
 *     (`ninguno_casa`).
 * Nunca da nada por confirmado: eso lo hace el admin.
 */
export function proponerRecinto<T extends CandidatoSigpac>(areaNaveHa: number | null, candidatos: readonly T[]): PropuestaSigpac<T> {
  const area = areaNaveHa != null && Number.isFinite(areaNaveHa) && areaNaveHa > 0 ? areaNaveHa : null;
  const tol = area != null ? toleranciaHa(area) : null;
  const distancia = (c: T) => {
    const s = num(c.superficie_ha);
    return area == null || s == null ? Infinity : Math.abs(s - area);
  };
  const ordenados = [...candidatos].sort(
    (a, b) =>
      Number(usoCompatible(b.uso_sigpac)) - Number(usoCompatible(a.uso_sigpac)) ||
      (b.muestras ?? 0) - (a.muestras ?? 0) ||
      distancia(a) - distancia(b),
  );
  const base = { candidatos: ordenados, area_nave_ha: area, tolerancia_ha: tol };

  if (ordenados.length === 0) return { estado: "sin_propuesta", motivo: "sin_candidatos", propuesto: null, ...base };
  if (area == null) return { estado: "dudoso", motivo: "sin_area", propuesto: null, ...base };

  const total = ordenados.reduce((s, c) => s + (c.muestras ?? 0), 0);
  const casan = ordenados.filter((c) => casaSuperficie(area, c.superficie_ha));
  let motivo: MotivoPropuesta | null = null;
  if (casan.length > 1) motivo = "varios_parecidos";
  else if (casan.length === 1) {
    const unico = casan[0];
    // ¿Cae ahí la nave? Al menos la mitad de los puntos que contestaron. Sin muestras
    // (un recinto suelto guardado sin ellas) vale si es el único candidato.
    const domina = total === 0 ? ordenados.length === 1 : (unico.muestras ?? 0) * 2 >= total;
    if (!usoCompatible(unico.uso_sigpac)) motivo = "uso_no_compatible";
    else if (!domina) motivo = "no_domina";
    else return { estado: "propuesto", motivo: "superficie_casa", propuesto: unico, ...base };
  }

  // ¿La nave es algo mayor que el recinto compatible donde más cae, y cae casi entera
  // en él? El solape se cuenta sobre los puntos que cayeron en recintos compatibles:
  // los que caen en el camino o la acequia del borde son el dibujo que se sale, no
  // otra nave. El que más se lleva va primero en `ordenados`.
  const compatibles = ordenados.filter((c) => usoCompatible(c.uso_sigpac));
  const principal = compatibles[0];
  if (principal && naveAlgoMayor(area, principal.superficie_ha)) {
    const totalCompatible = compatibles.reduce((s, c) => s + (c.muestras ?? 0), 0);
    const casiEntera = totalCompatible === 0 ? ordenados.length === 1 : (principal.muestras ?? 0) >= SOLAPE_ALTO * totalCompatible - 1e-9;
    if (casiEntera) return { estado: "propuesto", motivo: "nave_algo_mayor", propuesto: principal, ...base };
    return { estado: "dudoso", motivo: motivo ?? "nave_mayor_varios", propuesto: null, ...base };
  }
  return { estado: "dudoso", motivo: motivo ?? "ninguno_casa", propuesto: null, ...base };
}

/**
 * Qué se guarda de lo que dijo el WMS: si la superficie casa, SOLO el recinto
 * propuesto (los caminos que cazó un punto del borde no ensucian la nave); si la
 * nave es algo mayor que el propuesto o hay dudas, los candidatos MÁS IDÓNEOS (como
 * mucho `MAX_CANDIDATOS`, ya ordenados), para marcar cuáles son suyos: una nave
 * dibujada con los bordes puede pisar además el recinto de al lado. Nada, si no hay
 * ninguno. Con la nave algo mayor, de los de al lado solo los de uso compatible (el
 * camino que cazó un punto del borde no es de la nave).
 */
export function aGuardar<T extends CandidatoSigpac>(p: PropuestaSigpac<T>): T[] {
  if (p.estado === "propuesto" && p.propuesto && p.motivo === "superficie_casa") return [p.propuesto];
  // Algo mayor: el propuesto y los de al lado que puedan ser suyos (no el camino del borde).
  if (p.estado === "propuesto" && p.propuesto) return p.candidatos.filter((c) => c === p.propuesto || usoCompatible(c.uso_sigpac)).slice(0, MAX_CANDIDATOS);
  return p.candidatos.slice(0, MAX_CANDIDATOS);
}
